// RunningTrackArtAdsComponent.js
//
// 런닝머신앱 «트랙 광고판» — 트랙 옆 광고 링(14장이 한 띠)에 걸리는 미디어아트 광고.
// 사진을 올리지 않는다. 운영자는 테마를 고르고 한 줄 문구와 노출 기간만 넣는다.
//   서버: /api/runningtrack/artads/*  (runningtrackartads)
//   앱  : TrackMediaArtRing 이 테마별 배경 효과 + 글자 등장 연출을 그린다.
// 공지(RunningTrackNoticeComponent)와 같은 편집 모델 — 카드 인라인 편집 → 「적용하기」로 일괄 저장.
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import client, { url } from '../../lib/api/client';
import ArtAdPreview, { ART_THEMES, themeOf } from './ArtAdPreview';

// 테마 토큰·미리보기는 ArtAdPreview.js (유니티 소스 기준). 새 테마는 앱·서버·거기 셋을 같이 고친다.
// 문구 상한 250자(사용자 결정 2026-09-09) — 서버 ART_AD_TEXT_MAX 와 같다. 넘치면 입력에서 막는다.
//   ★긴 문구는 앱이 링 둘레(147 m)에 맞게 글자를 줄여 한 바퀴에 한 번 건다 — 짧을수록 크게 읽힌다.
const MAX_TEXT = 250;
const MAX_ADS = 10;

const Container = styled.div`
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #fff;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  margin: 0 0 16px 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: bold;
  display: flex;
  align-items: baseline;
  gap: 10px;

  & small {
    font-size: 0.8rem;
    font-weight: 400;
    color: #888;
  }
`;

const InfoBox = styled.div`
  background-color: #f5f5f5;
  border-left: 4px solid #1976d2;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-radius: 4px;
  font-size: 13px;
  color: #555;
  line-height: 1.8;
`;

const Card = styled.div`
  display: flex;
  gap: 16px;
  padding: 14px;
  border: 2px solid ${props => props.inactive ? '#eee' : '#e3f2fd'};
  border-radius: 10px;
  margin-bottom: 12px;
  background-color: ${props => props.inactive ? '#fafafa' : '#fff'};
  opacity: ${props => props.inactive ? 0.65 : 1};
`;

const OrderColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 40px;
  flex-shrink: 0;
`;

const OrderIndex = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1976d2;
`;

const OrderButton = styled.button`
  width: 26px;
  height: 22px;
  border: 1px solid #e0e0e0;
  background: #fff;
  border-radius: 4px;
  font-size: 11px;
  color: #666;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.35 : 1};
  padding: 0;
`;

const FieldColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: ${props => props.flex || 1};
  min-width: 0;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #888;
`;

const FieldInput = styled.input`
  padding: 8px 10px;
  border: 2px solid ${props => props.invalid ? '#e53935' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.invalid ? '#e53935' : '#1976d2'};
  }
`;

const ThemeRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ThemeChip = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 2px solid ${props => props.selected ? '#1976d2' : 'transparent'};
  background: ${props => props.bg};
  color: ${props => props.ink};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: ${props => props.selected ? '0 0 0 2px rgba(25,118,210,.25)' : 'inset 0 0 0 1px rgba(0,0,0,.12)'};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${props => props.invalid ? '#e53935' : '#888'};
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  white-space: nowrap;
`;

const SmallButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${props => props.danger ? '#ef9a9a' : '#e0e0e0'};
  background: ${props => props.danger ? '#ffebee' : '#fff'};
  color: ${props => props.danger ? '#c62828' : '#555'};
  font-size: 12px;
  cursor: pointer;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  border: none;
  background-color: ${props => props.primary ? '#4A90E2' : '#f0f0f0'};
  color: ${props => props.primary ? '#fff' : '#555'};
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

const textLen = (text) => Array.from(text || '').length;
const textTooLong = (text) => textLen(text) > MAX_TEXT;

// unix 초 ↔ <input type="date"> 문자열. 브라우저 로컬(= 매장) 기준.
const toDateInput = (sec) => {
  if (!sec) return '';
  const d = new Date(sec * 1000);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};
const startOfDay = (str) => (str ? Math.floor(new Date(`${str}T00:00:00`).getTime() / 1000) : 0);
const endOfDay = (str) => (str ? Math.floor(new Date(`${str}T23:59:59`).getTime() / 1000) : 0);

const isLiveNow = (ad) => {
  if (Number(ad.active) === 0) return false;
  const now = Math.floor(Date.now() / 1000);
  if (ad.startTime && ad.startTime > now) return false;
  if (ad.endTime && ad.endTime < now) return false;
  return true;
};

const RunningTrackArtAdsComponent = () => {
  const [ads, setAds] = useState([]);
  const [originalAds, setOriginalAds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { club } = useSelector(({ club }) => ({ club: club.defaultClub }));

  const loadAds = async () => {
    if (!club) return;
    try {
      setIsLoading(true);
      const res = await client.get(url + '/api/runningtrack/artads/' + club.clubID);
      const list = (res.data.artAds || []).map(ad => ({
        ...ad,
        startTime: Number(ad.startTime) || 0,
        endTime: Number(ad.endTime) || 0,
        active: Number(ad.active) === 0 ? 0 : 1,
      }));
      setAds(list);
      setOriginalAds(JSON.parse(JSON.stringify(list)));
    } catch (error) {
      console.error('Failed to load art ads:', error);
      alert('트랙 광고판 광고를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadAds(); }, [club]);

  const isModified = JSON.stringify(ads) !== JSON.stringify(originalAds);

  // 추가 — 서버가 ID 와 순서를 정하므로 등록 후 다시 읽어온다.
  const handleAdd = async () => {
    if (!club) return;
    if (ads.length >= MAX_ADS) {
      alert('트랙 광고판 광고는 최대 ' + MAX_ADS + '개까지 등록할 수 있습니다.');
      return;
    }
    if (isModified) {
      alert('저장하지 않은 변경사항이 있습니다. 먼저 적용하기를 눌러주세요.');
      return;
    }
    try {
      setIsLoading(true);
      await client.post(url + '/api/runningtrack/artads/' + club.clubID, {
        theme: 'petal',
        text: '오늘도 한 바퀴 더',
        startTime: 0,
        endTime: 0,
        active: 1,
      });
      await loadAds();
    } catch (error) {
      console.error('Failed to add art ad:', error);
      alert('광고 추가에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm('이 광고를 삭제하시겠습니까?')) return;
    try {
      setIsLoading(true);
      await client.delete(url + '/api/runningtrack/artads/' + ad.runningTrackArtAdsID);
      await loadAds();
    } catch (error) {
      console.error('Failed to delete art ad:', error);
      alert('광고 삭제에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    const next = JSON.parse(JSON.stringify(ads));
    next[index][field] = value;
    setAds(next);
  };

  const handleMove = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= ads.length) return;
    const next = ads.slice();
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    setAds(next);
  };

  const handleSave = async () => {
    if (!club || !isModified) return;

    for (let i = 0; i < ads.length; i++) {
      const text = (ads[i].text || '').trim();
      if (!text) { alert((i + 1) + '번 광고의 문구를 입력해주세요.'); return; }
      if (textTooLong(text)) {
        alert((i + 1) + '번 광고의 문구가 너무 깁니다. ' + MAX_TEXT + '자 이내로 줄여주세요.');
        return;
      }
      if (ads[i].startTime && ads[i].endTime && ads[i].endTime < ads[i].startTime) {
        alert((i + 1) + '번 광고의 노출 종료일이 시작일보다 앞섭니다.');
        return;
      }
    }

    try {
      setIsLoading(true);
      for (let i = 0; i < ads.length; i++) {
        const ad = ads[i];
        const origin = originalAds.find(o => o.runningTrackArtAdsID === ad.runningTrackArtAdsID);
        const changed = !origin
          || origin.theme !== ad.theme
          || origin.text !== ad.text
          || origin.startTime !== ad.startTime
          || origin.endTime !== ad.endTime
          || Number(origin.active) !== Number(ad.active);
        if (changed) {
          const res = await client.patch(url + '/api/runningtrack/artads/' + ad.runningTrackArtAdsID, {
            theme: ad.theme,
            text: (ad.text || '').trim(),
            startTime: ad.startTime || 0,
            endTime: ad.endTime || 0,
            active: Number(ad.active) === 0 ? 0 : 1,
          });
          if (res && res.data && res.data.result !== 1) {
            alert((i + 1) + '번 광고 저장 실패: ' + (res.data.error || ''));
            return;
          }
        }
      }

      const orderChanged = ads.map(a => a.runningTrackArtAdsID).join(',')
        !== originalAds.map(a => a.runningTrackArtAdsID).join(',');
      if (orderChanged) {
        await client.post(url + '/api/runningtrack/artads/' + club.clubID + '/order', {
          artAdsIDs: ads.map(a => a.runningTrackArtAdsID),
        });
      }

      await loadAds();
      alert('트랙 광고판 광고가 저장되었습니다.');
    } catch (error) {
      console.error('Failed to save art ads:', error);
      alert('저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Title>런닝머신앱 트랙 광고판 <small>미디어아트 · 테마 + 한 줄 문구</small></Title>
      <InfoBox>
        트랙 옆 광고 링(14장이 한 띠)에 <strong>테마 배경 효과 위에 문구 한 줄</strong>이 20초씩 순서대로 걸립니다.
        사진은 올리지 않습니다 — 테마를 고르고 문구와 노출 기간만 넣으세요.
        문구는 <strong>{MAX_TEXT}자</strong>까지. 길수록 글자가 작아지니(링 한 바퀴에 맞춰 축소) 짧을수록 멀리서 잘 읽힙니다.
        아래 미리보기는 앱과 같은 배경·글자 연출입니다(실기 20초 사이클을 9초로 압축).
        노출 기간을 비우면 항상 노출됩니다. 등록된 광고가 하나도 없으면 앱 기본 문구 5종이 돕니다.
      </InfoBox>

      {ads.map((ad, index) => {
        const theme = themeOf(ad.theme);
        const tooLong = textTooLong(ad.text);
        const live = isLiveNow(ad);
        return (
          <Card key={ad.runningTrackArtAdsID} inactive={!live}>
            <OrderColumn>
              <OrderIndex>{index + 1}</OrderIndex>
              <OrderButton disabled={index === 0 || isLoading} onClick={() => handleMove(index, -1)}>▲</OrderButton>
              <OrderButton disabled={index === ads.length - 1 || isLoading} onClick={() => handleMove(index, 1)}>▼</OrderButton>
            </OrderColumn>

            <FieldColumn>
              <FieldGroup>
                <FieldLabel>테마</FieldLabel>
                <ThemeRow>
                  {ART_THEMES.map(t => (
                    <ThemeChip
                      key={t.key}
                      type="button"
                      bg={t.chipBg}
                      ink={t.chipInk}
                      selected={t.key === ad.theme}
                      title={t.desc}
                      onClick={() => handleChange(index, 'theme', t.key)}
                    >{t.name}</ThemeChip>
                  ))}
                </ThemeRow>
              </FieldGroup>

              <FieldRow>
                <FieldGroup flex={2}>
                  <FieldLabel>문구 · 한 줄</FieldLabel>
                  <FieldInput
                    type="text"
                    value={ad.text || ''}
                    invalid={tooLong}
                    placeholder="예) 오늘도 한 바퀴 더"
                    onChange={(e) => handleChange(index, 'text', e.target.value.replace(/[\r\n]+/g, ' '))}
                  />
                  <Hint invalid={tooLong}>
                    {textLen(ad.text)} / {MAX_TEXT}자
                    {tooLong ? ' — 너무 깁니다. 줄여주세요.' : ''}
                  </Hint>
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>노출 시작</FieldLabel>
                  <FieldInput
                    type="date"
                    value={toDateInput(ad.startTime)}
                    onChange={(e) => handleChange(index, 'startTime', startOfDay(e.target.value))}
                  />
                </FieldGroup>
                <FieldGroup>
                  <FieldLabel>노출 종료</FieldLabel>
                  <FieldInput
                    type="date"
                    value={toDateInput(ad.endTime)}
                    onChange={(e) => handleChange(index, 'endTime', endOfDay(e.target.value))}
                  />
                </FieldGroup>
              </FieldRow>

              <ArtAdPreview themeKey={theme.key} text={ad.text} />
            </FieldColumn>

            <SideColumn>
              <Toggle>
                <input
                  type="checkbox"
                  checked={Number(ad.active) !== 0}
                  onChange={(e) => handleChange(index, 'active', e.target.checked ? 1 : 0)}
                />
                노출
              </Toggle>
              <Hint>{live ? '지금 노출 중' : (Number(ad.active) === 0 ? '꺼짐' : '기간 밖')}</Hint>
              <SmallButton danger type="button" disabled={isLoading} onClick={() => handleDelete(ad)}>삭제</SmallButton>
            </SideColumn>
          </Card>
        );
      })}

      <Footer>
        <Button type="button" disabled={isLoading || ads.length >= MAX_ADS} onClick={handleAdd}>+ 광고 추가</Button>
        <Button primary type="button" disabled={isLoading || !isModified} onClick={handleSave}>적용하기</Button>
      </Footer>
    </Container>
  );
};

export default RunningTrackArtAdsComponent;
